import type { OfflineReport, StationState } from "@station-keeping/sim";
import { useState } from "react";
import { session, useSession, type BlockReason } from "../game/session";
import { formatDuration, formatNumber, t } from "../i18n";
import type { LoginLink } from "../platform/login-link";
import { Dialog } from "./Dialog";
import { apiErrorLabel, moduleName } from "./labels";
import { PixelText } from "./pixel";
import { Key, Led, ScoreRow, SectionTitle } from "./primitives";
import { UnlockList } from "./Progression";

const BLOCKED = {
  offline: { title: "blocked.offline.title", body: "blocked.offline.body", action: "retry" },
  upgrade: { title: "blocked.upgrade.title", body: "blocked.upgrade.body", action: "reload" },
  suspended: { title: "blocked.suspended.title", body: "blocked.suspended.body", action: null },
  unavailable: { title: "blocked.unavailable.title", body: "blocked.unavailable.body", action: "retry" },
  error: { title: "blocked.error.title", body: "blocked.error.body", action: "retry" },
} as const satisfies Record<BlockReason, { title: string; body: string; action: "retry" | "reload" | null }>;

/** La partie ne peut pas continuer : coupure, mise à jour obligatoire, compte suspendu, maintenance. */
export function BlockedDialog({ reason }: { reason: BlockReason }) {
  const screen = BLOCKED[reason];
  return (
    <Dialog
      title={t(screen.title)}
      footer={
        screen.action === "retry" ? (
          <Key tone="action" onClick={() => session.retry()} autoFocus>
            {t("blocked.retry")}
          </Key>
        ) : screen.action === "reload" ? (
          <Key tone="action" onClick={() => window.location.reload()} autoFocus>
            {t("blocked.reload")}
          </Key>
        ) : null
      }
    >
      <p className="mt-3 text-base text-muted">{t(screen.body)}</p>
    </Dialog>
  );
}

/** La station est ouverte ailleurs : cette partie est figée jusqu'à « Reprendre ici ». */
export function TakeoverDialog({ by }: { by: "device" | "tab" }) {
  return (
    <Dialog
      title={t(by === "tab" ? "takeover.tab.title" : "takeover.device.title")}
      footer={
        <Key tone="action" onClick={() => void session.takeOver()} autoFocus>
          {t("takeover.resume")}
        </Key>
      }
    >
      <p className="mt-3 text-base text-muted">{t(by === "tab" ? "takeover.tab.body" : "takeover.device.body")}</p>
    </Dialog>
  );
}

function names(state: StationState, ids: readonly string[]): string[] {
  return ids.flatMap((id) => {
    const module = state.modules.find((candidate) => candidate.id === id);
    return module ? [moduleName(module.type)] : [];
  });
}

/** « Pendant ton absence » : le score de la station sans le joueur, et ce qui mérite un coup d'œil. */
export function OfflineReportDialog({ report, state }: { report: OfflineReport; state: StationState }) {
  const builds = names(state, report.completedBuilds);
  const jobs = report.completedJobs.flatMap((job) => {
    const [name] = names(state, [job.moduleId]);
    if (!name) return [];
    if (job.kind === "upgrade") {
      const module = state.modules.find((candidate) => candidate.id === job.moduleId);
      return [t("event.upgraded", { module: name, level: module?.level ?? 1 })];
    }
    return [t(job.kind === "patch" ? "offline.job.patch" : "offline.job.repair", { module: name })];
  });
  const watch = names(state, report.watch);
  const quiet = report.credits === 0 && report.shipsServed === 0 && builds.length === 0 && jobs.length === 0;
  return (
    <Dialog
      title={t("offline.title")}
      footer={
        <Key tone="action" onClick={() => session.dismissOfflineReport()} autoFocus>
          {t("offline.close")}
        </Key>
      }
    >
      <p className="mt-3 text-base text-muted">
        {t("offline.away", { time: formatDuration(report.elapsedSeconds) })}
        {report.countedSeconds < report.elapsedSeconds ? ` ${t("offline.capped", { time: formatDuration(report.countedSeconds) })}` : ""}
      </p>
      <dl className="mt-3">
        <ScoreRow label={t("offline.credits")} value={`+${formatNumber(report.credits)} CR`} tone="var(--color-good)" />
        <ScoreRow label={t("offline.ships")} value={formatNumber(report.shipsServed)} />
        {report.xp > 0 ? <ScoreRow label={t("offline.xp")} value={`+${formatNumber(report.xp)} XP`} /> : null}
        {report.levelTo > report.levelFrom ? (
          <ScoreRow label={t("offline.level")} value={t("hud.levelValue", { level: report.levelTo })} tone="var(--color-accent)" />
        ) : null}
      </dl>
      {report.levelTo > report.levelFrom ? (
        <section>
          <SectionTitle>{t("level.up.title", { level: report.levelTo })}</SectionTitle>
          <UnlockList level={report.levelTo} detailed />
        </section>
      ) : null}
      {quiet ? <p className="mt-4 text-base text-muted">{t("offline.quiet")}</p> : null}
      {builds.length > 0 ? <ReportList title={t("offline.builds")} items={builds} tone="good" /> : null}
      {jobs.length > 0 ? <ReportList title={t("offline.jobs")} items={jobs} tone="good" /> : null}
      {watch.length > 0 ? <ReportList title={t("offline.watch")} hint={t("offline.watchHint")} items={watch} tone="warn" /> : null}
    </Dialog>
  );
}

function ReportList({ title, hint, items, tone }: { title: string; hint?: string; items: string[]; tone: "good" | "warn" }) {
  const color = tone === "warn" ? "var(--color-warn)" : "var(--color-good)";
  return (
    <section>
      <SectionTitle hint={hint}>{title}</SectionTitle>
      <ul className="flex flex-col">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="dotted flex items-center gap-2.5 py-2 text-base text-ink">
            <Led on color={color} blink={tone === "warn"} />
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Lien de connexion ouvert alors qu'un compte joue déjà ici : on demande avant de changer de compte (la
 * station actuelle reste sur le sien).
 */
export function LoginLinkDialog({ link }: { link: LoginLink }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await session.verifyCode(link.email, link.code);
      session.consumeLoginLink();
    } catch (failure) {
      setError(apiErrorLabel(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t("loginLink.title")}
      footer={
        <>
          <Key tone="action" onClick={() => void confirm()} disabled={busy} autoFocus>
            {t("loginLink.confirm")}
          </Key>
          <Key tone="quiet" onClick={() => session.consumeLoginLink()} disabled={busy}>
            {t("account.cancel")}
          </Key>
        </>
      }
    >
      <p className="mt-3 text-base text-muted">{t("loginLink.body", { email: link.email })}</p>
      {error ? (
        <p role="alert" className="mt-3 text-base text-stop">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}

/** Sous le fronton : annonce du studio, mise à jour disponible, liaison perdue, synchronisation en cours. */
export function StatusBar() {
  const announcement = useSession((state) => state.config?.settings.announcement ?? null);
  const updateAvailable = useSession((state) => state.updateAvailable);
  const network = useSession((state) => state.network);
  const reopening = useSession((state) => state.reopening);
  const playing = useSession((state) => state.phase.kind === "playing");
  if (!announcement && !updateAvailable && !(playing && (network === "offline" || reopening))) return null;
  return (
    <div className="mt-2 flex flex-col items-center gap-2 px-3">
      {announcement ? (
        <p className="bezel pointer-events-auto max-w-xl">
          <span className="flex items-center gap-3 px-1 text-[0.9375rem] text-ink">
            <Led on color="var(--color-accent)" />
            {announcement}
          </span>
        </p>
      ) : null}
      {updateAvailable ? (
        <div className="bezel pointer-events-auto">
          <div className="flex items-center gap-3 pl-1 text-[0.9375rem] text-ink">
            <Led on blink color="var(--color-night)" />
            {t("update.available")}
            <Key size="sm" tone="quiet" onClick={() => window.location.reload()}>
              {t("update.reload")}
            </Key>
          </div>
        </div>
      ) : null}
      {playing && network === "offline" ? (
        <p className="bezel pointer-events-auto" title={t("sync.offlineHint")}>
          <span className="flex items-center gap-2 px-1 text-warn">
            <Led on blink color="var(--color-warn)" />
            <PixelText text={t("sync.offline")} />
            <span className="sr-only">{t("sync.offlineHint")}</span>
          </span>
        </p>
      ) : playing && reopening ? (
        <p className="bezel pointer-events-auto">
          <span className="flex items-center gap-2 px-1 text-muted">
            <Led on blink color="var(--color-accent)" />
            <PixelText text={t("sync.reopening")} />
          </span>
        </p>
      ) : null}
    </div>
  );
}
